// Platform + device context for Guard Web.
//
// The native iOS/Android app is the primary product; this web build is a companion that must KNOW
// it is the web surface and adapt per device (Nick's 3-surface requirement). This module is the
// single source of truth for "what can this browser/device actually do" — capability detection that
// downstream capture code branches on (e.g. Web NFC is Android-Chrome-only, so QR is the web
// primary; camera presence gates the QR scanner; geolocation gates GPS capture). Pure + injectable
// so it is exhaustively unit-testable without a real browser.

export type Surface = "web";
export type FormFactor = "phone" | "tablet" | "desktop";

export interface DeviceContext {
  /** Always "web" here — this is how the app knows it is the web surface, not native. */
  surface: Surface;
  formFactor: FormFactor;
  /** Web NFC (NDEFReader) — secure-context Android-Chrome only. Absent on iOS Safari and desktop. */
  hasWebNfc: boolean;
  /** getUserMedia present (needed for the QR scanner; also requires a secure context at runtime). */
  hasCamera: boolean;
  hasGeolocation: boolean;
  /** Launched as an installed PWA (standalone display mode). */
  isStandalonePwa: boolean;
}

function media(win: Window, query: string): boolean {
  try {
    return typeof win.matchMedia === "function" && win.matchMedia(query).matches === true;
  } catch {
    return false;
  }
}

/** Detect the running device's surface + capabilities. `win` is injectable for testing. */
export function detectDeviceContext(win: Window = window): DeviceContext {
  const nav: Navigator | undefined = win.navigator;
  const coarse = media(win, "(pointer: coarse)");
  const wide = media(win, "(min-width: 900px)");
  // Wide + fine pointer reads as desktop; wide + coarse reads as a tablet; otherwise a phone.
  const formFactor: FormFactor = wide ? (coarse ? "tablet" : "desktop") : "phone";
  const standalone =
    media(win, "(display-mode: standalone)") ||
    (nav as unknown as { standalone?: boolean } | undefined)?.standalone === true;

  return {
    surface: "web",
    formFactor,
    hasWebNfc: win.isSecureContext === true && "NDEFReader" in win,
    hasCamera:
      typeof nav?.mediaDevices === "object" &&
      nav.mediaDevices !== null &&
      typeof nav.mediaDevices.getUserMedia === "function",
    hasGeolocation: typeof nav?.geolocation === "object" && nav.geolocation !== null,
    isStandalonePwa: standalone === true,
  };
}

/** Short human label for the running surface — used for "you're on the web app" cues + per-device copy. */
export function describeDevice(ctx: DeviceContext): string {
  const parts: string[] = [ctx.formFactor, "web"];
  if (ctx.hasWebNfc) {
    parts.push("NFC");
  }
  return parts.join(" · ");
}
